/**
 * ScreenSkeleton — Loading skeleton variants for self-serve screens.
 * Built on the existing Skeleton primitive from ShadCN.
 */
import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

/**
 * Plan screen skeleton — cards matching Your Audience / What You'll Get layout
 */
export function PlanSkeleton() {
  return (
    <div className="space-y-3 sm:space-y-4 animate-pulse">
      {/* Hero */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex-1">
          <Skeleton className="h-7 w-64 mb-2" />
          <Skeleton className="h-4 w-80" />
        </div>
        <div className="text-right">
          <Skeleton className="h-3 w-12 mb-1 ml-auto" />
          <Skeleton className="h-8 w-24 ml-auto" />
        </div>
      </div>

      {/* Card 1 */}
      <Card>
        <CardHeader className="py-3 px-3 sm:px-6">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-56 mt-1" />
        </CardHeader>
        <CardContent className="px-3 sm:px-6 pt-0">
          <Skeleton className="h-16 w-full mb-3" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Skeleton className="h-28 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-3 w-40" />
              <Skeleton className="h-3 w-48" />
              <Skeleton className="h-3 w-44" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card 2 */}
      <Card>
        <CardHeader className="py-3 px-3 sm:px-6">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-56 mt-1" />
        </CardHeader>
        <CardContent className="px-3 sm:px-6 pt-0">
          <div className="grid grid-cols-2 gap-2.5 mb-4">
            <Skeleton className="h-24 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
          </div>
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>

      {/* Card 3 */}
      <Card>
        <CardHeader className="py-3 px-3 sm:px-6">
          <Skeleton className="h-4 w-28" />
        </CardHeader>
        <CardContent className="px-3 sm:px-6 pt-0 space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Review screen skeleton — video player + sidebar
 */
export function ReviewSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Video area */}
        <div>
          <Skeleton className="aspect-video w-full rounded-xl" />
          <div className="flex gap-2 mt-3">
            <Skeleton className="h-9 w-24 rounded-lg" />
            <Skeleton className="h-9 w-24 rounded-lg" />
          </div>
        </div>
        {/* Sidebar */}
        <Card className="hidden lg:block">
          <CardContent className="p-4 space-y-3">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-8 w-full mt-4" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * Dashboard skeleton — campaign card list
 */
export function DashboardSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Skeleton className="h-7 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-36 rounded-lg" />
      </div>

      {/* Campaign cards */}
      {[1, 2].map((i) => (
        <Card key={i}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
            <Skeleton className="h-2 w-full rounded-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Report skeleton — metric cards + chart
 */
export function ReportSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      {/* Hero */}
      <div className="flex items-center gap-3 mb-6">
        <Skeleton className="h-6 w-20 rounded-full" />
        <Skeleton className="h-7 w-64" />
      </div>

      {/* Metric cards */}
      <Card>
        <CardContent className="p-4">
          <Skeleton className="h-5 w-40 mb-4" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      <Card>
        <CardContent className="p-4">
          <Skeleton className="h-5 w-48 mb-3" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </CardContent>
      </Card>

      {/* Benchmarks */}
      <Skeleton className="h-32 w-full rounded-xl" />
    </div>
  );
}
